import React, { useEffect, useState } from 'react';
import { Task } from '../../lib/tasks';
import { getTasks, updateTaskStatus } from '../../lib/tasks';
import { useBusiness } from '../../contexts/BusinessContext';
import { format, parseISO } from 'date-fns';
import { Calendar, Trash2 } from 'lucide-react';
import { useSupabase } from '../../contexts/SupabaseContext';
import { useTasks } from '../../contexts/TasksContext';

interface RecurringTask {
  id: string;
  title: string;
  startTime: string;
  dueTime: string;
  dayOfWeek: number;
  priority: string;
}

const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export default function RecurringTasksOverview() {
  const { selectedBusiness } = useBusiness();
  const { tasks, selectedResource } = useTasks();
  const supabase = useSupabase();
  const [recurringTasks, setRecurringTasks] = useState<RecurringTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    try {
      setLoading(true);
      
      // Filter for recurring tasks and group by unique patterns
      const uniqueRecurringTasks = new Map<string, RecurringTask>();
      
      tasks.forEach(task => {
        if (task.is_recurring && task.recurrence_frequency === 'weekly' &&
            (!selectedResource || task.resource_id === selectedResource)) {
          const startDate = parseISO(task.start_date);
          const dueDate = task.due_date ? parseISO(task.due_date) : null;
          const dayOfWeek = startDate.getDay();
          const startTime = format(startDate, 'h:mm a');
          const dueTime = dueDate ? format(dueDate, 'h:mm a') : '';
          
          // Create a unique key based on title and time
          const key = `${task.title}-${startTime}-${dayOfWeek}`;
          
          // Only add if we haven't seen this pattern before
          if (!uniqueRecurringTasks.has(key)) {
            uniqueRecurringTasks.set(key, {
              id: task.id,
              title: task.title,
              startTime,
              dueTime,
              dayOfWeek,
              priority: task.priority
            });
          }
        }
      });

      setRecurringTasks(Array.from(uniqueRecurringTasks.values()));
      setError(null);
    } catch (err) {
      console.error('Error processing recurring tasks:', err);
      setError('Failed to process recurring tasks');
    } finally {
      setLoading(false);
    }
  }, [tasks, selectedResource]);

  const getPriorityColor = (priority: string) => {
    switch (priority.toLowerCase()) {
      case 'high':
        return 'bg-red-100 text-red-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return <div className="p-4 text-center text-gray-500">Loading recurring tasks...</div>;
  }

  if (error) {
    return <div className="p-4 text-center text-red-500">{error}</div>;
  }

  if (recurringTasks.length === 0) {
    return <div className="p-4 text-center text-gray-500">No recurring tasks found</div>;
  }

  return (
    <div className="p-4">
      <div className="grid grid-cols-1 gap-4">
        {DAYS_OF_WEEK.map((day, index) => {
          const dayTasks = recurringTasks.filter(task => task.dayOfWeek === index);
          if (dayTasks.length === 0) return null;

          return (
            <div key={day} className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-900 mb-3">{day}</h3>
              <div className="space-y-3">
                {dayTasks.map(task => (
                  <div key={task.id} className="bg-white rounded-lg border border-gray-200 p-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-gray-900 truncate">{task.title}</h4>
                        <div className="flex items-center gap-2 mt-2">
                          <div className="flex items-center text-xs text-gray-500">
                            <Calendar className="h-3.5 w-3.5 mr-1" />
                            {task.startTime}
                            {task.dueTime && ` - ${task.dueTime}`}
                          </div>
                          <div className={`px-1.5 py-0.5 text-xs font-medium rounded-full ${getPriorityColor(task.priority)}`}>
                            {task.priority}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
} 